import React, { FC, ReactNode, useMemo, useState } from 'react';
import { Collapse, Paper, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';

import { ICalendarEventsParams } from 'src/api/dtos';
import { Checkbox, CollapsibleLabel, Status, TeamIcon } from 'src/shared/components';
import { FeaturesEnums, PermissionsEnums } from 'src/shared/enums';
import { ComponentWithFeatures, ComponentWithPermissions } from 'src/shared/hocs';
import { useFeatures } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { RootState } from 'src/store/types';
import './MonthlyCalendarFilters.styles.scss';

type FilterTypes = Pick<
  ICalendarEventsParams,
  | 'includeApprovedAbsences'
  | 'includePendingAbsences'
  | 'includeRejectedAbsences'
  | 'includePartialDayAbsences'
  | 'includeSchedules'
  | 'includeDepartmentAbsences'
  | 'includeAvailabilities'
  | 'includeShiftPickups'
  | 'includeShiftOffers'
  | 'includeDepartmentSchedules'
>;

interface IFilterInfo {
  label: string;
  key: keyof FilterTypes;
  status?: ReactNode;
}

interface Props {
  filters: FilterTypes;
  isPartialAbsenceEnabled: boolean;
  disabled: boolean;
  onFilter: (filters: FilterTypes) => void;
}

export const MonthlyCalendarFilters: FC<Props> = ({ filters, disabled, isPartialAbsenceEnabled, onFilter }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const { settings } = useSelector((state: RootState) => state.user);
  const hasScheduledAbsenceFeature = useFeatures(FeaturesEnums.Schedule_Employee_Absences);
  const hasShiftSwapping = useFeatures(FeaturesEnums.ShiftSwapping);
  const shiftSwapEnabled = !!settings?.isShiftSwapEnabled && hasShiftSwapping;

  const [openAbsences, setOpenAbsences] = useState<boolean>(true);
  const [openSchedules, setOpenSchedules] = useState<boolean>(true);
  const [openDepartment, setOpenDepartment] = useState<boolean>(true);

  const absenceStatuses: IFilterInfo[] = useMemo(() => {
    const scheduledAbsenceFilter: IFilterInfo[] = hasScheduledAbsenceFeature
      ? [
          {
            label: t('absences.approved'),
            key: 'includeApprovedAbsences',
            status: <Status variant="success" size="medium" />,
          },
        ]
      : [];

    const partialAbsenceFilter: IFilterInfo[] = isPartialAbsenceEnabled
      ? [{ label: t('absences.partialDay'), key: 'includePartialDayAbsences' }]
      : [];

    return [
      ...scheduledAbsenceFilter,
      {
        label: t('absences.pending'),
        key: 'includePendingAbsences',
        status: <Status variant="warning" size="medium" />,
      },
      {
        label: t('absences.rejected'),
        key: 'includeRejectedAbsences',
        status: <Status variant="error" size="medium" />,
      },
      ...partialAbsenceFilter,
    ];
  }, []);

  return (
    <Paper
      variant="elevation"
      id="monthly-calendar-filters"
      className={`p-3 calendar-filters ${theme.palette.mode === 'light' ? 'bg-transparent' : ''}`}
    >
      <CollapsibleLabel
        label={t('absences.myAbsences')}
        in={openAbsences}
        onClick={() => setOpenAbsences(!openAbsences)}
      />
      <Collapse in={openAbsences} timeout="auto" unmountOnExit>
        {absenceStatuses.map(({ label, key, status }, index) => {
          return (
            <div key={`${label}-${index}`} className="d-flex align-items-center justify-content-between">
              <Checkbox
                className="small d-block"
                label={<span className="small">{label}</span>}
                checked={filters[key]}
                disabled={disabled}
                onCheck={() => onFilter({ [key]: !filters[key] })}
              />
              <div>{status}</div>
            </div>
          );
        })}
      </Collapse>
      <ComponentWithFeatures features={FeaturesEnums.Scheduling_Function}>
        <div className="mt-3 pt-3">
          <CollapsibleLabel
            label={t('calendar.schedules')}
            in={openSchedules}
            onClick={() => setOpenSchedules(!openSchedules)}
          />
          <Collapse in={openSchedules} timeout="auto" unmountOnExit>
            <div className="d-flex align-items-center justify-content-between">
              <Checkbox
                className="small d-block"
                label={<span className="small">{t('calendar.schedule')}</span>}
                checked={filters.includeSchedules}
                disabled={disabled}
                onCheck={() => onFilter({ includeSchedules: !filters.includeSchedules })}
              />
              <Status variant="info" size="medium" />
            </div>
            <ComponentWithFeatures features={FeaturesEnums.SchedulingAvailability}>
              <div className="d-flex align-items-center justify-content-between">
                <Checkbox
                  className="small d-block"
                  label={<span className="small">{t('availability.availability')}</span>}
                  checked={filters.includeAvailabilities}
                  disabled={disabled}
                  onCheck={() => onFilter({ includeAvailabilities: !filters.includeAvailabilities })}
                />
              </div>
            </ComponentWithFeatures>
            {shiftSwapEnabled && (
              <div className="d-flex align-items-center justify-content-between">
                <Checkbox
                  className="small d-block"
                  label={<span className="small">{t('shiftSwaps.pickupShifts')}</span>}
                  checked={filters.includeShiftPickups}
                  disabled={disabled}
                  onCheck={() => onFilter({ includeShiftPickups: !filters.includeShiftPickups })}
                />
                <Status variant="danger" size="medium" />
              </div>
            )}
            <ComponentWithPermissions permissions={PermissionsEnums.FEATURE_SHIFT_OFFER}>
              <div className="d-flex align-items-center justify-content-between">
                <Checkbox
                  className="small d-block"
                  label={<span className="small">{t('calendar.offeredShifts')}</span>}
                  checked={filters.includeShiftOffers}
                  disabled={disabled}
                  onCheck={() => onFilter({ includeShiftOffers: !filters.includeShiftOffers })}
                />
                <Status variant="warning" size="medium" />
              </div>
            </ComponentWithPermissions>
          </Collapse>
        </div>
      </ComponentWithFeatures>
      <ComponentWithPermissions permissions={PermissionsEnums.Employee_Calendar_VIEW_MY_DEPARTMENT_ABSENCES}>
        <div className="mt-3 pt-3">
          <CollapsibleLabel
            label={
              <div className="d-flex align-items-center">
                <TeamIcon size={18} className="me-2" />
                {t('calendar.department')}
              </div>
            }
            in={openDepartment}
            onClick={() => setOpenDepartment(!openDepartment)}
          />
          <Collapse in={openDepartment} timeout="auto" unmountOnExit>
            <Checkbox
              className="small d-block"
              label={<span className="small">{t('calendar.absences')}</span>}
              checked={filters.includeDepartmentAbsences}
              disabled={disabled}
              onCheck={() => onFilter({ includeDepartmentAbsences: !filters.includeDepartmentAbsences })}
            />
          </Collapse>
          <Checkbox
            className="small d-block"
            label={<span className="small">{t('calendar.schedules')}</span>}
            checked={filters.includeDepartmentSchedules}
            disabled={disabled}
            onCheck={() => onFilter({ includeDepartmentSchedules: !filters.includeDepartmentSchedules })}
          />
        </div>
      </ComponentWithPermissions>
    </Paper>
  );
};
